import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { NAV_LINKS, SITE } from "../data/site";

export const Nav = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-nav"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-500 ${
                scrolled || open
                    ? "bg-cream/85 backdrop-blur-xl shadow-[0_1px_0_rgba(14,90,97,0.1)]"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-16 sm:h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <a href="#acasa" className="flex items-center gap-2.5" data-testid="nav-logo" onClick={() => setOpen(false)}>
                    <img src="/logo.png" alt="Sigla Ține(te) de noi" className="h-9 w-9 sm:h-11 sm:w-11 rounded-full object-cover ring-1 ring-teal/10" />
                    <span className="font-serif text-lg sm:text-xl font-semibold text-teal leading-none">
                        Ține<span className="text-coral">(te)</span> de noi
                    </span>
                </a>

                <nav className="hidden md:flex items-center gap-7" aria-label="Navigație principală">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.href.slice(1)}`}
                            className="link-underline text-sm font-medium text-charcoal/80 hover:text-teal transition-colors duration-300"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <a
                        href={SITE.phoneHref}
                        data-testid="nav-call-button"
                        className="hidden sm:inline-flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-teal-dark"
                    >
                        <Phone className="h-4 w-4" />
                        {SITE.phoneDisplay}
                    </a>
                    <button
                        type="button"
                        aria-label={open ? "Închide meniul" : "Deschide meniul"}
                        data-testid="nav-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-full bg-teal/5 text-teal ring-1 ring-teal/15"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        key="mobile-menu"
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="md:hidden overflow-hidden bg-cream/95 backdrop-blur-xl border-t border-teal/10"
                        aria-label="Meniu mobil"
                    >
                        <div className="px-5 py-4 flex flex-col">
                            {NAV_LINKS.map((l, i) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                                    onClick={() => setOpen(false)}
                                    className={`py-4 font-serif text-2xl text-teal ${i > 0 ? "border-t border-teal/10" : ""}`}
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href={SITE.phoneHref}
                                data-testid="nav-mobile-call-button"
                                className="mt-3 mb-2 inline-flex items-center justify-center gap-2 rounded-full bg-coral py-4 text-base font-semibold text-cream"
                            >
                                <Phone className="h-5 w-5" />
                                Sună: {SITE.phoneDisplay}
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
